import { createSlice } from '@reduxjs/toolkit';
import { PROTOCOL_STATUS } from 'features/stepByStepProtocol/utils/protocol/constants';

// Only source data lives here (FE plan section 10). The resolved path and the suggestions are
// derived in selectors. Protocols are keyed by `locationId:gateDirection`, so N transactions on one
// lane share one read and one parse. Only transactions whose location has a protocol are attached.
const initialState = {
    transactions: {},
    protocols: {},
};

const stepByStepProtocolSlice = createSlice({
    name: 'stepByStepProtocol',
    initialState,
    reducers: {
        attachTransaction(state, { payload: { transactionId, protocolKey } }) {
            if (transactionId == null || !protocolKey) return;
            // The same transaction can arrive again (a resend); an open panel stays open.
            const current = state.transactions[transactionId];
            state.transactions[transactionId] = { protocolKey, panelOpen: current?.protocolKey === protocolKey && current.panelOpen };
        },

        detachTransaction(state, { payload: transactionId }) {
            delete state.transactions[transactionId];
        },

        setPanelOpen(state, { payload: { transactionId, open } }) {
            const entry = state.transactions[transactionId];
            if (entry) entry.panelOpen = !!open;
        },

        // Dispatched only when there is no AVAILABLE entry yet. Refreshing an AVAILABLE protocol after
        // the cache TTL skips it, so open panels keep their content until the new read settles.
        protocolLoadStarted(state, { payload: { protocolKey } }) {
            state.protocols[protocolKey] = { status: PROTOCOL_STATUS.LOADING, definition: null, meta: null, loadedAt: null, error: null };
        },

        protocolLoadSettled(state, { payload: { protocolKey, status, definition = null, meta = null, error = null, loadedAt } }) {
            const current = state.protocols[protocolKey];
            // A refresh that cannot be read (UNAVAILABLE) keeps the content that was valid, so open panels and
            // the Protocol suggestions stay; the old `loadedAt` keeps it stale, so the next transaction on
            // the lane reads it again. Any other result replaces it, an INVALID one too: a broken upload
            // must not leave old content around, and only an AVAILABLE read carries a definition.
            if (status === PROTOCOL_STATUS.UNAVAILABLE && current?.status === PROTOCOL_STATUS.AVAILABLE) {
                current.error = error;
                return;
            }
            state.protocols[protocolKey] = {
                status,
                definition: status === PROTOCOL_STATUS.AVAILABLE ? definition : null,
                meta,
                loadedAt,
                error,
            };
        },

        resetAll() {
            return initialState;
        },

        // The Agent picked a transport type in the Detected select; the saga applies it to the form.
        transportTypeSelected() {},
    },
});

export const stepByStepProtocolActions = stepByStepProtocolSlice.actions;
export const stepByStepProtocolReducer = stepByStepProtocolSlice.reducer;

// For the host rail, which pins one panel at a time (Relays, Info or Protocol).
export const setProtocolPanelOpen = (transactionId, open) => stepByStepProtocolActions.setPanelOpen({ transactionId, open });
